import { Badge } from '@/components/ui/badge';
import { AuditStatus, ExceptionStatus, AssigneeStatus } from '@/types/asset-audit';
import { cn } from '@/lib/utils';

interface StatusBadgeProps {
  readonly status: AuditStatus | ExceptionStatus | AssigneeStatus;
  readonly className?: string;
  readonly size?: 'sm' | 'md' | 'lg';
}

const auditStatusConfig: Record<
  AuditStatus,
  { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline'; className?: string }
> = {
  [AuditStatus.Draft]: {
    label: 'Draft',
    variant: 'outline',
    className: 'bg-gray-50 text-gray-700 border-gray-300',
  },
  [AuditStatus.Scheduled]: {
    label: 'Scheduled',
    variant: 'secondary',
    className: 'bg-blue-50 text-blue-700 border-blue-200',
  },
  [AuditStatus.Assigned]: {
    label: 'Assigned',
    variant: 'secondary',
    className: 'bg-purple-50 text-purple-700 border-purple-200',
  },
  [AuditStatus.InProgress]: {
    label: 'In Progress',
    variant: 'default',
    className: 'bg-amber-50 text-amber-700 border-amber-200',
  },
  [AuditStatus.UnderReview]: {
    label: 'Under Review',
    variant: 'secondary',
    className: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  },
  [AuditStatus.Completed]: {
    label: 'Completed',
    variant: 'default',
    className: 'bg-green-50 text-green-700 border-green-200',
  },
  [AuditStatus.Cancelled]: {
    label: 'Cancelled',
    variant: 'destructive',
    className: 'bg-red-50 text-red-700 border-red-200',
  },
  [AuditStatus.OnHold]: {
    label: 'On Hold',
    variant: 'outline',
    className: 'bg-orange-50 text-orange-700 border-orange-200',
  },
};

const exceptionStatusConfig: Record<
  ExceptionStatus,
  { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline'; className?: string }
> = {
  [ExceptionStatus.Open]: {
    label: 'Open',
    variant: 'destructive',
    className: 'bg-red-50 text-red-700 border-red-200',
  },
  [ExceptionStatus.InProgress]: {
    label: 'In Progress',
    variant: 'default',
    className: 'bg-amber-50 text-amber-700 border-amber-200',
  },
  [ExceptionStatus.Resolved]: {
    label: 'Resolved',
    variant: 'default',
    className: 'bg-green-50 text-green-700 border-green-200',
  },
  [ExceptionStatus.Closed]: {
    label: 'Closed',
    variant: 'secondary',
    className: 'bg-gray-50 text-gray-700 border-gray-300',
  },
  [ExceptionStatus.UnderReview]: {
    label: 'Under Review',
    variant: 'secondary',
    className: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  },
  [ExceptionStatus.ActionRequired]: {
    label: 'Action Required',
    variant: 'destructive',
    className: 'bg-orange-50 text-orange-700 border-orange-200',
  },
  [ExceptionStatus.Escalated]: {
    label: 'Escalated',
    variant: 'destructive',
    className: 'bg-red-100 text-red-800 border-red-300',
  },
};

const assigneeStatusConfig: Record<
  AssigneeStatus,
  { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline'; className?: string }
> = {
  [AssigneeStatus.Pending]: {
    label: 'Pending',
    variant: 'outline',
    className: 'bg-gray-50 text-gray-700 border-gray-300',
  },
  [AssigneeStatus.Accepted]: {
    label: 'Accepted',
    variant: 'default',
    className: 'bg-blue-50 text-blue-700 border-blue-200',
  },
  [AssigneeStatus.InProgress]: {
    label: 'In Progress',
    variant: 'default',
    className: 'bg-amber-50 text-amber-700 border-amber-200',
  },
  [AssigneeStatus.Completed]: {
    label: 'Completed',
    variant: 'default',
    className: 'bg-green-50 text-green-700 border-green-200',
  },
  [AssigneeStatus.Declined]: {
    label: 'Declined',
    variant: 'destructive',
    className: 'bg-red-50 text-red-700 border-red-200',
  },
};

export function StatusBadge({ status, className, size = 'md' }: StatusBadgeProps) {
  // Determine which status type it is
  const isAuditStatus = Object.values(AuditStatus).includes(status as AuditStatus);
  const isExceptionStatus = Object.values(ExceptionStatus).includes(status as ExceptionStatus);
  const isAssigneeStatus = Object.values(AssigneeStatus).includes(status as AssigneeStatus);
  
  let config: { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline'; className?: string };
  
  if (isAuditStatus) {
    config = auditStatusConfig[status as AuditStatus];
  } else if (isExceptionStatus) {
    config = exceptionStatusConfig[status as ExceptionStatus];
  } else if (isAssigneeStatus) {
    config = assigneeStatusConfig[status as AssigneeStatus];
  } else {
    config = { label: String(status), variant: 'outline' };
  }

  const sizeClasses = {
    sm: 'text-xs px-2 py-0.5',
    md: 'text-sm px-2.5 py-0.5',
    lg: 'text-base px-3 py-1',
  };

  return (
    <Badge
      variant={config.variant}
      className={cn(
        'font-medium border',
        config.className,
        sizeClasses[size],
        className
      )}
    >
      {config.label}
    </Badge>
  );
}
